import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://fowhwonigjfcqrkwbpzz.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZvd2h3b25pZ2pmY3Fya3dicHp6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODI3OTQwNDYsImV4cCI6MjA5ODM3MDA0Nn0.Bm6d74fgb-iGmYgFtt_RBlPjoCcrccWe1z9trDTWmoQ';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Fetch tools for the catalog page — supports category filter and sort.
 */
export async function fetchTools({ category = 'All', sort = 'score' } = {}) {
  let q = supabase.from('tools').select('*');

  if (category && category !== 'All') {
    q = q.eq('category', category);
  }

  if (sort === 'score') q = q.order('score', { ascending: false });
  if (sort === 'name')  q = q.order('name', { ascending: true });
  if (sort === 'trend') q = q.order('trending', { ascending: false }).order('score', { ascending: false });

  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

/**
 * Plain keyword search against the tools table (used for instant client-side filtering,
 * e.g. typing in the catalog search box — no AI call, just ilike matching).
 */
export async function quickSearchTools(term) {
  if (!term) return fetchTools();
  const { data, error } = await supabase
    .from('tools')
    .select('*')
    .or(`name.ilike.%${term}%,tagline.ilike.%${term}%,description.ilike.%${term}%,category.ilike.%${term}%`)
    .order('score', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Full hybrid AI search — calls the Supabase Edge Function which does:
 * keyword/full-text search (Postgres) -> Claude reasoning + re-rank -> combined result.
 * Used by the homepage hero search bar.
 */
export async function aiSearchTools(query) {
  const { data, error } = await supabase.functions.invoke('ai-search', {
    body: { query },
  });
  if (error) throw error;
  return data; // { query, reasoning, results }
}

/**
 * Fetch all published blog posts for the listing page.
 */
export async function fetchPosts() {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('published', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single post by slug for the detail page.
 */
export async function fetchPost(slug) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('slug', slug)
    .eq('published', true)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Distinct list of categories present in the data, for filter pills.
 */
export async function fetchCategories() {
  const { data, error } = await supabase.from('tools').select('category');
  if (error) throw error;
  const unique = Array.from(new Set((data ?? []).map(t => t.category))).sort();
  return ['All', ...unique];
}

/**
 * Subscribe an email to the newsletter.
 */
export async function subscribeNewsletter(email) {
  const { error } = await supabase
    .from('subscribers')
    .insert({ email: email.toLowerCase().trim() });
  if (error) {
    if (error.code === '23505') throw new Error('already_subscribed');
    throw error;
  }
}

/**
 * Sign up a new user with email + password via Supabase Auth.
 */
export async function signUp(email, password, name) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: name } },
  });
  if (error) throw error;
  return data;
}

/**
 * Sign in an existing user.
 */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

/**
 * Sign out the current user.
 */
export async function signOut() {
  await supabase.auth.signOut();
}

/**
 * Get the current session/user.
 */
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/* ─── Stack helpers ─── */

/** Get IDs of all tools the current user has saved */
export async function getMyStackIds() {
  const { data, error } = await supabase.rpc('get_my_stack');
  if (error) throw error;
  return new Set((data ?? []).map(r => r.tool_id));
}

/** Get full tool details for the current user's stack */
export async function getMyStackTools() {
  const { data, error } = await supabase.rpc('get_my_stack_tools');
  if (error) throw error;
  return data ?? [];
}

/** Save a tool to the current user's stack */
export async function saveToStack(toolId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('not_authenticated');
  const { error } = await supabase
    .from('stacks')
    .insert({ user_id: session.user.id, tool_id: toolId });
  if (error && error.code !== '23505') throw error; // ignore duplicate
}

/** Remove a tool from the current user's stack */
export async function removeFromStack(toolId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('not_authenticated');
  const { error } = await supabase
    .from('stacks')
    .delete()
    .eq('user_id', session.user.id)
    .eq('tool_id', toolId);
  if (error) throw error;
}

/**
 * Returns the best URL for a tool:
 * affiliate_url if it exists and is a real link, otherwise website_url.
 * Use this everywhere a tool link appears — never hardcode website_url directly.
 */
export function getToolUrl(tool) {
  if (
    tool.affiliate_url &&
    tool.affiliate_url !== '' &&
    !tool.affiliate_url.startsWith('[YOUR_')
  ) {
    return tool.affiliate_url;
  }
  return tool.website_url;
}
